import { create } from 'zustand';
import { Updater } from '@tanstack/react-table';
import {
  QuickFilter,
  TableDefinitionDto,
} from '@/components/table/types/TableDefinitionDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import loadsh from 'lodash';
import {
  SingleFilterForm,
  FilterCriteriaForm,
} from '@/components/table/schemas/filterCriteriaSchema.ts';
import { TablePreferences } from '@/components/table/preferences/tablePreferences.ts';
import { UrlFilter } from '@/components/table/routes/listSearchParams.ts';
import { urlFiltersToFilterForm } from '@/components/table/routes/urlFilters.ts';

const applyUpdater = <T>(updater: Updater<T>, old: T): T =>
  typeof updater === 'function' ? (updater as (old: T) => T)(old) : updater;

/**
 * Domyślne wartości tylko dla id jeszcze nieobecnych: definicja przychodzi ponownie przy każdym
 * odświeżeniu zapytania (`useColumns`) i nie może cofać przełączeń użytkownika ani wartości z adresu.
 */
export const mergeQuickFilterState = (
  current: Record<string, boolean>,
  quickFilters: QuickFilter[] | undefined,
): Record<string, boolean> =>
  Object.fromEntries(
    (quickFilters ?? []).map((qf) => [
      qf.id,
      Object.prototype.hasOwnProperty.call(current, qf.id)
        ? current[qf.id]
        : qf.enabledByDefault,
    ]),
  );

export interface TableStoreProps {
  pageRequest: PageRequest;
  tableDefinition: TableDefinitionDto;
  filterCriteria: FilterCriteriaForm;
  searchTerm: string;
  quickFilterState: Record<string, boolean>;
  /** Filtry z adresu czekające na definicję pól — bez niej nie wiadomo, jakiego są typu. */
  pendingUrlFilters: UrlFilter[] | null;
  rowSelection: Record<string, boolean>;
  /**
   * Full row objects for the current selection, keyed by id. Accumulated as the
   * user selects so selected-row data survives page changes and cache eviction
   * (selection is tracked purely by id, independent of which page is loaded).
   */
  selectedRowsById: Record<string, RowWithId>;
  exportHandler: (() => void) | null;
  // Column preferences (width / order / visibility)
  columnOrder: string[];
  columnVisibility: Record<string, boolean>;
  columnSizing: Record<string, number>;
  defaultColumnPreferences: TablePreferences | null;
  columnPreferencesReady: boolean;
  // Actions
  setPageRequest: (pageRequest: PageRequest) => void;
  setTableDefinition: (tableDefinition: TableDefinitionDto) => void;
  setFilters: (filters: SingleFilterForm[]) => void;
  setFiltersCombinator: (combinator: 'AND' | 'OR') => void;
  setSearchTerm: (searchTerm: string) => void;
  addQuickFilter: (filter: Record<string, boolean>) => void;
  setRowSelection: (
    selection:
      | Record<string, boolean>
      | ((old: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  clearRowSelection: () => void;
  /** Reconciles `selectedRowsById` with the current page rows + selection. */
  syncSelectedRows: (
    rows: RowWithId[],
    rowSelection: Record<string, boolean>,
  ) => void;
  setExportHandler: (handler: (() => void) | null) => void;
  setColumnOrder: (updater: Updater<string[]>) => void;
  setColumnVisibility: (updater: Updater<Record<string, boolean>>) => void;
  setColumnSizing: (updater: Updater<Record<string, number>>) => void;
  applyColumnPreferences: (
    preferences: TablePreferences,
    defaults: TablePreferences,
  ) => void;
  resetColumnPreferences: () => void;
}

export const createTableStore = ({
  pageSize,
  searchTerm = '',
  quickFilters = {},
  urlFilters,
}: {
  pageSize: number;
  searchTerm?: string;
  quickFilters?: Record<string, boolean>;
  urlFilters?: UrlFilter[];
}) => {
  return create<TableStoreProps>((set) => ({
    pageRequest: {
      page: 0,
      size: pageSize,
    },
    tableDefinition: {
      fields: [],
      quickFilters: [],
    },
    filterCriteria: {
      combinator: 'AND',
      filters: [],
    },
    searchTerm,
    quickFilterState: quickFilters,
    pendingUrlFilters: urlFilters?.length ? urlFilters : null,
    rowSelection: {},
    selectedRowsById: {},
    exportHandler: null,
    columnOrder: [],
    columnVisibility: {},
    columnSizing: {},
    defaultColumnPreferences: null,
    columnPreferencesReady: false,
    // Actions
    setPageRequest: (pageRquest) => set({ pageRequest: pageRquest }),
    setTableDefinition: (tableDefinition) =>
      set((state) => ({
        tableDefinition,
        quickFilterState: mergeQuickFilterState(
          state.quickFilterState,
          tableDefinition.quickFilters,
        ),
        ...(state.pendingUrlFilters && {
          filterCriteria: {
            ...state.filterCriteria,
            filters: urlFiltersToFilterForm(
              tableDefinition,
              state.pendingUrlFilters,
            ),
          },
          pendingUrlFilters: null,
        }),
      })),
    setFilters: (filters) => {
      set((state) => {
        if (loadsh.isEqual(state.filterCriteria.filters, filters)) return state;
        return {
          filterCriteria: {
            ...state.filterCriteria,
            filters: filters,
          },
          pageRequest: {
            ...state.pageRequest,
            page: 0,
          },
        };
      });
    },
    setFiltersCombinator: (combinator) => {
      set((state) => {
        if (state.filterCriteria.combinator === combinator) return state;
        return {
          filterCriteria: {
            ...state.filterCriteria,
            combinator: combinator,
          },
          pageRequest: {
            ...state.pageRequest,
            page: 0,
          },
        };
      });
    },
    setSearchTerm: (searchTerm) => {
      set((state) => ({
        searchTerm,
        pageRequest: {
          ...state.pageRequest,
          page: 0,
        },
      }));
    },
    addQuickFilter: (filter) => {
      set((state) => ({
        quickFilterState: {
          ...state.quickFilterState,
          ...filter,
        },
        pageRequest: {
          ...state.pageRequest,
          page: 0,
        },
      }));
    },
    setRowSelection: (selection) =>
      set((state) => {
        const newSelection =
          typeof selection === 'function'
            ? selection(state.rowSelection)
            : selection;
        if (loadsh.isEqual(state.rowSelection, newSelection)) {
          return state;
        }
        return { rowSelection: newSelection };
      }),
    clearRowSelection: () =>
      set((state) =>
        Object.keys(state.rowSelection).length === 0 &&
        Object.keys(state.selectedRowsById).length === 0
          ? state
          : { rowSelection: {}, selectedRowsById: {} },
      ),
    syncSelectedRows: (rows, rowSelection) =>
      set((state) => {
        const next: Record<string, RowWithId> = {};
        // Keep already-captured rows that are still selected (other pages)...
        for (const id of Object.keys(state.selectedRowsById)) {
          if (rowSelection[id]) next[id] = state.selectedRowsById[id];
        }
        // ...and capture/refresh selected rows visible on the current page.
        for (const row of rows) {
          if (rowSelection[row.id]) next[row.id] = row;
        }
        if (loadsh.isEqual(state.selectedRowsById, next)) return state;
        return { selectedRowsById: next };
      }),
    setExportHandler: (handler) => set({ exportHandler: handler }),
    setColumnOrder: (updater) =>
      set((state) => ({
        columnOrder: applyUpdater(updater, state.columnOrder),
      })),
    setColumnVisibility: (updater) =>
      set((state) => ({
        columnVisibility: applyUpdater(updater, state.columnVisibility),
      })),
    setColumnSizing: (updater) =>
      set((state) => ({
        columnSizing: applyUpdater(updater, state.columnSizing),
      })),
    applyColumnPreferences: (preferences, defaults) =>
      set({
        columnOrder: preferences.columnOrder,
        columnVisibility: preferences.columnVisibility,
        columnSizing: preferences.columnSizing,
        defaultColumnPreferences: defaults,
        columnPreferencesReady: true,
      }),
    resetColumnPreferences: () =>
      set((state) => {
        if (!state.defaultColumnPreferences) return state;
        return {
          columnOrder: state.defaultColumnPreferences.columnOrder,
          columnVisibility: state.defaultColumnPreferences.columnVisibility,
          columnSizing: state.defaultColumnPreferences.columnSizing,
        };
      }),
  }));
};

export type TableStore = ReturnType<typeof createTableStore>;
